import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { ReactComponent as AttachmentIcon } from 'assets/icons/attachment.svg';
import { RangeSlider } from 'components/RangeSlider';
import MediaPlayButton from 'components/MediaPlayButton';
import InputMediaManager from 'styles/Input';
import ThresholdContext from './ThresholdContext';
import AmountSelector from './AmountSelector';
import DeleteButton from './DeleteButton';

import AmountMin from './styles/AmountMin';
import MediaFile from './styles/MediaFieldContainer';
import InputWrapper from './styles/InputWrapper';
import Options from './styles/Options';

const SoundSelector = ({ currentVolume }) => {
  function getAudioFile(mediumId, list) {
    if (mediumId) return list.find(medium => medium.id === mediumId);
    return undefined;
  }

  const { config, media, onTriggerShowMediaManager, onChange } = useContext(ThresholdContext);
  const [audioFile, setAudioFile] = useState(getAudioFile(config.mediumId, media));
  const [audioFileName, setAudioFileName] = useState('');

  useEffect(() => {
    setAudioFile(getAudioFile(config.mediumId, media));
  }, [config.mediumId, media]);

  useEffect(() => {
    setAudioFileName(audioFile ? audioFile.name : '');
    if (!audioFile) onChange({ mediumId: '' });
  }, [audioFile]);

  // Use currentVolume if provided (during dragging), otherwise fall back to config.volume
  const volume = typeof currentVolume === 'number' ? currentVolume : config.volume;

  return (
    <MediaFile>
      <InputMediaManager
        value={audioFileName}
        readOnly
        style={{ cursor: 'pointer' }}
        onClick={onTriggerShowMediaManager}
        placeholder="Nie wybrano żadnego dźwięku"
        hoverBorderColor="var(--brand-blue)"
      />
      {audioFile && <MediaPlayButton audioFile={audioFile} volume={volume} />}
      <AttachmentIcon />
    </MediaFile>
  );
};

SoundSelector.propTypes = {
  currentVolume: PropTypes.number,
};

SoundSelector.defaultProps = {
  currentVolume: undefined,
};

const VolumeSelectorRoot = styled('div')({
  maxWidth: 180,
  minWidth: 180,
  margin: '0 auto 0 0',
  paddingLeft: 5,
});

const VolumeSelector = ({ onVolumeChange }) => {
  const { config, handleChange, isUpdating } = useContext(ThresholdContext);
  const [innerVolume, setInnerVolume] = useState(config.volume * 100);

  useEffect(() => {
    setInnerVolume(Math.round(config.volume * 100));
  }, [config.volume]);

  const handleChangeValue = value => {
    setInnerVolume(value);
    // Notify parent of volume change in real-time (0-1 range)
    if (onVolumeChange) {
      onVolumeChange(value / 100);
    }
  };

  const handleAfterChange = value => {
    handleChange('volume', value / 100);
  };

  return (
    <VolumeSelectorRoot>
      <RangeSlider
        position="horizontal"
        value={innerVolume}
        min={1}
        max={100}
        onChange={handleChangeValue}
        onAfterChange={handleAfterChange}
        disabled={isUpdating}
      />
    </VolumeSelectorRoot>
  );
};

VolumeSelector.propTypes = {
  onVolumeChange: PropTypes.func,
};

VolumeSelector.defaultProps = {
  onVolumeChange: undefined,
};

const AudioThreshold = () => {
  const { isDefault, config } = useContext(ThresholdContext);
  const hoverColor = 'var(--brand-blue)';
  const [currentVolume, setCurrentVolume] = useState(config.volume);

  // Update currentVolume when config.volume changes (e.g., from external source)
  useEffect(() => {
    setCurrentVolume(config.volume);
  }, [config.volume]);

  return (
    <div>
      <InputWrapper>
        <div style={{ flex: 1 }}>
          <SoundSelector currentVolume={currentVolume} />
        </div>

        {!isDefault && <DeleteButton />}
      </InputWrapper>

      <Options between>
        <VolumeSelector onVolumeChange={setCurrentVolume} />

        {!isDefault && (
          <AmountMin>
            <span>odtwarzany od kwoty:</span>
            <AmountSelector hoverColor={hoverColor} />
          </AmountMin>
        )}
      </Options>
    </div>
  );
};

export default AudioThreshold;
